import React, { useState, useEffect, useRef } from 'react';
import {
	Container, Grid, GridItem, Heading, Button, Link as ChakraLink, useBreakpointValue, Wrap, WrapItem,
	Breadcrumb, BreadcrumbItem, BreadcrumbLink, Icon, Flex,
	Box, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { FontAwesomeIcon as Fa } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faChevronRight, faHome, fas,
} from '@fortawesome/free-solid-svg-icons';
import { decode } from 'jsonwebtoken';
import * as Sentry from '@sentry/nextjs';
import Page from '../../components/Page';
import { serversideInstance as axios } from '../../utils/axios';
import Graph from '../../components/graph/Graph';
import { findBreadcrumb } from '../../utils/nav';
import * as CustomGraph from '../../components/customGraph';
import Dataset from '../../components/graph/Dataset';
import collectView from '../../utils/collectView';

export default function Home({
	table, graphs, lastUpdate, views,
}) {
	// const GraphComponent = dynamic(() => import(`../../components/graph/${tableName.toLowerCase()}`));
	const [bigTitle, setBigTitle] = useState(true);
	const [showAll, setShowAll] = useState(false);

	const [showAccordion, setShowAcordion] = useState(false);
	const accordionButtonSubmit = useRef();

	useEffect(() => {
		setBigTitle(!showAll);
	}, [showAll]);

	useEffect(() => {
		if (table?.description && !showAccordion) {
			accordionButtonSubmit.current.click();
		}
	}, [table]);

	const maxColSpan = useBreakpointValue({
		base: 1,
		md: 3,
		lg: 4,
	});

	library.add(fas);
	function GraphIcon({ type }) {
		const graphIcons = [
			{ type: 'bar', name: '長條圖', icon: 'chart-bar' },
			{ type: 'line', name: '折線圖', icon: 'chart-line' },
			{ type: 'radial', name: '圓餅圖', icon: 'chart-pie' },
			{ type: 'map', name: '地圖（熱力圖）', icon: 'map' },
			{ type: 'mapwithoutcontainer', name: '地圖（點位）', icon: 'map' },
			{ type: 'treemap', name: '矩形樹圖', icon: 'table' },
			{ type: 'dataset', name: '清冊', icon: 'list' },
		];

		const graphIcon = graphIcons.find((x) => x.type === type);
		return (
			<Icon as={Fa} icon={graphIcon?.icon} />
		);
	}

	return (
		<Page views={views} title={table?.name ?? ''}>
			<Flex flexDirection={{ base: 'column', md: 'row' }} justifyContent="space-between">
				<Breadcrumb separator={<Icon as={Fa} icon={faChevronRight} />} mb={4} fontSize="sm">
					<BreadcrumbItem verticalAlign="bottom">
						<Icon as={Fa} icon={faHome} mr={1} />
						<BreadcrumbLink href="/">新北市藝文儀表板</BreadcrumbLink>
					</BreadcrumbItem>
					{findBreadcrumb(table.shortName).map((x, i) => (
						<BreadcrumbItem key={`${x}${i}['name']`} verticalAlign="bottom">
							<BreadcrumbLink href={x.link ?? '#'}>{x.name}</BreadcrumbLink>
						</BreadcrumbItem>
					))}
					<BreadcrumbItem verticalAlign="bottom">
						<BreadcrumbLink href="#">{table.name}</BreadcrumbLink>
					</BreadcrumbItem>
				</Breadcrumb>
				<Box fontSize="sm">
					上次更新時間：
					{dayjs(lastUpdate).format('YYYY/MM')}
				</Box>
			</Flex>
			{table?.description ? (
				<Box my={4}>
					<Accordion allowToggle defaultIndex={[0]}>
						<AccordionItem borderWidth="0" borderBottom="0">
							<Flex alignItems="center">
								<Heading as="h1" mb={2} fontSize="2xl">
									{table.name}
								</Heading>
								<AccordionButton p={0} ml={2} width="20px" ref={accordionButtonSubmit} onClick={() => setShowAcordion(!showAccordion)}>
									<AccordionIcon />
								</AccordionButton>
							</Flex>
							<AccordionPanel pb={4} px={0}>
								{table?.description}
							</AccordionPanel>
						</AccordionItem>
					</Accordion>
				</Box>
			) : (
				<Heading as="h1" my={4} fontSize="2xl">
					{table.name}
				</Heading>
			)}
			<Wrap
				display={{ base: 'none', md: 'block' }}
				w="95%"
				mx="auto"
				justify="center"
				position={showAll ? 'inherit' : 'sticky'}
				top="25px"
				zIndex={999}
				bgColor="rgba(0, 20, 73, 0.8)"
				py={4}
			>
				{graphs.map((g) => (
					<WrapItem key={`btn${g.shortName}`}>
						<Button
							as={ChakraLink}
							href={`#${g.shortName}`}
							colorScheme="blue"
							size="sm"
							mx={1}
							onClick={() => setShowAll(false)}
							bgColor="transparent"
							color="white"
							variant="outline"
							leftIcon={<GraphIcon type={g.type} />}
							_hover={{
								color: 'primary',
								bgColor: 'whiteAlpha.300',
							}}
							_active={{
								color: 'primary',
								bgColor: 'whiteAlpha.300',
							}}
						>
							{g.name}
						</Button>
					</WrapItem>
				))}
				<WrapItem>
					<Button
						colorScheme="blue"
						size="sm"
						mx={1}
						onClick={() => setShowAll(true)}
						bgColor="transparent"
						color="white"
						variant="outline"
						_hover={{
							color: 'primary',
							bgColor: 'whiteAlpha.300',
						}}
						_active={{
							color: 'primary',
							bgColor: 'whiteAlpha.300',
						}}
					>
						全部顯示
					</Button>
				</WrapItem>
			</Wrap>
			<Container w="100%" maxW={showAll ? '' : 'container.xl'}>
				<Grid
					templateColumns={{
						base: 'minmax(0, 1fr)',
						md: showAll ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)' : '',
						lg: showAll ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)' : '',
					}}
					gridGap={5}
				>

					{graphs?.filter((g) => !g.type.startsWith('dataset')).map((g) => {
						switch (g.type.toString().toLowerCase()) {
						case 'map':
							return (
								<GridItem
									key={g.shortName}
									my={10}
									colSpan={Math.min(maxColSpan, showAll ? 2 : 1)}
								>
									{CustomGraph[g.shortName] ? CustomGraph[g.shortName]({ data: g.data, bigTitle }) : null}
								</GridItem>
							);
						case 'mapwithoutcontainer':
							return (
								<GridItem
									key={g.shortName}
									my={10}
									colSpan={Math.min(maxColSpan, showAll ? maxColSpan : 1)}
								>
									{CustomGraph[g.shortName] ? CustomGraph[g.shortName]({ data: g.data, bigTitle, table: g.table }) : null}
								</GridItem>
							);
						case 'line':
						case 'bar':
						case 'treemap':
						case 'radial':
						default: {
							let colSpan = 1;
							let h = 200;
							switch (g.type.toString().toLowerCase()) {
							case 'treemap':
								h = 400;
								colSpan = showAll ? 2 : 1;
								break;
							case 'radial':
								h = 300;
								colSpan = showAll ? 2 : 1;
								break;
							default:
							}
							if (showAll) h = 250;

							return (
								<GridItem
									key={g.shortName}
									my={10}
									colSpan={Math.min(maxColSpan, colSpan)}
								>
									<Graph
										shortName={g.shortName}
										type={g.type}
										data={g.data}
										bigTitle={bigTitle}
										h={`${h}px`}
									/>
								</GridItem>
							);
						}
						}
					})}
					{graphs?.filter((g) => g.type.toString().toLowerCase() === 'dataset').map((g) => (
						<GridItem
							key={g.shortName}
							my={10}
							colSpan={Math.min(maxColSpan, showAll ? maxColSpan : 1)}
						>
							<Dataset
								shortName={g.shortName}
								data={g.data}
								table={g.table}
								bigTitle={bigTitle}
							/>
						</GridItem>
					))}
				</Grid>
			</Container>
		</Page>
	);
}

export async function getServerSideProps(ctx) {
	const token = ctx.req.cookies?.token;
	const info = token ? decode(token) : null;
	const expire = info ? info.exp * 1000 < Date.now() : false;
	if (info && !expire) {
		const { iat, exp, ...rest } = info;
		Sentry.setUser(rest);
	}

	const views = await collectView(ctx);
	const { table: tableName } = ctx.query;
	const { cookies } = ctx.req;
	const cookieHeaders = (cookies?.token && cookies?.['token.sig']) ? {
		headers: {
			Cookie: `token=${cookies.token}; token.sig=${cookies['token.sig']}`,
		},
	} : {};
	const { data: graphs } = await axios.get(`/api/v1/graphs?table=${tableName}`, cookieHeaders);

	if (!graphs?.length) {
		return {
			notFound: true,
		};
	}

	const broken = [];

	try {
		await axios.get(`/api/v1/table/${tableName}`, cookieHeaders);
	} catch (e) {
		switch (e?.response?.status) {
		case 404:
			return {
				notFound: true,
			};
		default:
			throw new Error('500');
		}
	}
	const { data: table } = await axios.get(`/api/v1/table/${tableName}`, cookieHeaders);

	let lastUpdate = new Date('1970-01-01');
	await Promise.all(graphs.map(async (_g) => {
		const g = _g;
		const startDate = !g.shortName.includes('GA317M05') && !g.shortName.includes('GA317M06') ? '1970-01-01' : dayjs().format('YYYY-01-01');
		try {
			const { headers, data } = await axios.get(`/api/v1/graph/${g.shortName}?start=${startDate}'`, cookieHeaders);
			const lu = new Date(headers['x-last-update']);
			if (lu > lastUpdate) {
				lastUpdate = lu;
			}
			g.data = data;
			g.table = table;
		} catch (e) {
			broken.push(g.shortName);
		}
	}));

	return {
		props: {
			views,
			tableName,
			table,
			graphs: graphs.filter((x) => !broken.includes(x.shortName)),
			lastUpdate: lastUpdate.toISOString(),
		},
	};
}
